import {
  afterNextRender,
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  input,
  Input,
  OnChanges,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  IChangedRouteStop,
  IRouteStopOption,
  IRouteStopRow,
  IRouteStopsPayload,
  ITextMatchPart,
  ITownStopOption,
} from 'src/app/models';
import {
  LiteralService,
  MapUtilsService,
  UtilsService,
} from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-route-ordered-stops',
  templateUrl: './route-ordered-stops.component.html',
  styleUrl: './route-ordered-stops.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteOrderedStopsComponent implements OnChanges, AfterViewChecked {
  @Input() townStops: ITownStopOption[] = [];
  @Input() townLatitude?: number;
  @Input() townLongitude?: number;
  @Input() applying = false;
  @Input() initialRows: IRouteStopRow[] = [];
  @Input() selectedStopIds: number[] = [];
  readonly allowReorderStops = input<boolean>(false);
  readonly revealMissingSegmentsRequest = input(0);
  @Output() confirmEventEmitter = new EventEmitter<IRouteStopsPayload>();
  @Output() selectStopEventEmitter = new EventEmitter<number | null>();
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('openedRow') openedRow?: ElementRef<HTMLElement>;
  @ViewChild('sideActions') sideActions?: ElementRef<HTMLElement>;
  @ViewChildren('rowElement') rowElements?: QueryList<ElementRef<HTMLElement>>;

  public images = images;
  public rows: IRouteStopRow[] = [this.newRow(), this.newRow()];
  public openedRowIndex: number | null = null;
  public searchTerm = '';
  public activeSearchRow: IRouteStopRow | null = null;
  public activeSearchMatchIndex = 0;
  public readonly missingSegmentWarningTops = signal<number[]>([]);

  private confirmTimeout: any;
  private highlightMissingSegments = false;
  private scrollToFirstMissingSegment = false;
  private lastEmittedKey = '';
  private lastSyncedGraphKey = '';
  private syncedStopChains: number[][] = [];
  private scrollToOpenedRow = false;
  private scrollToActiveSearchRow = false;
  private searchPartsByName = new Map<string, ITextMatchPart[]>();
  private changedStop: IChangedRouteStop | null = null;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private mapUtilsService: MapUtilsService,
    private liveAnnouncer: LiveAnnouncer,
    private injector: Injector,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['revealMissingSegmentsRequest'] && this.revealMissingSegmentsRequest() > 0)
      this.revealMissingSegments();
    if (changes['initialRows']) this.refreshMissingSegmentWarnings();
    if (changes['initialRows']) {
      const previousGraphKey = this.lastSyncedGraphKey;
      this.syncRowsWithGraph();
      const changedStop =
        this.lastSyncedGraphKey !== previousGraphKey ? this.changedStop : null;
      const changedRowIndex = changedStop
        ? this.rows.reduce(
            (closestIndex, row, index) =>
              row.stopId != null &&
              this.rowKey(row) === changedStop.key &&
              (closestIndex === -1 ||
                Math.abs(index - changedStop.rowIndex) <
                  Math.abs(closestIndex - changedStop.rowIndex))
                ? index
                : closestIndex,
            -1,
          )
        : -1;
      if (changedRowIndex !== -1) {
        if (this.openedRowIndex !== changedRowIndex)
          this.scrollToOpenedRow = true;
        this.openedRowIndex = changedRowIndex;
        setTimeout(() => this.emitOpenedRowSelection());
        return;
      }
    }
    if (changes['initialRows'] || changes['selectedStopIds'])
      this.syncOpenedRowWithSelection();
  }

  private syncRowsWithGraph() {
    // The rows mirror the map graph: rebuild them only when the graph itself changed,
    // so selection-triggered recomputes never wipe rows the user is editing
    const incomingGraphKey = JSON.stringify(
      (this.initialRows ?? []).map((row) => this.rowKey(row)),
    );
    if (incomingGraphKey === this.lastSyncedGraphKey) return;
    const hadSyncedRows =
      this.lastSyncedGraphKey !== '' && this.lastSyncedGraphKey !== '[]';
    const incomingStopChains = this.stopChainsOf(this.initialRows ?? []);
    this.lastSyncedGraphKey = incomingGraphKey;
    if (!this.initialRows?.length) {
      this.syncedStopChains = incomingStopChains;
      if (hadSyncedRows) this.reset();
      return;
    }
    const currentRowsKey = JSON.stringify(
      this.rows.map((row) => this.rowKey(row)),
    );
    if (currentRowsKey === incomingGraphKey) {
      this.rows.forEach(
        (row, index) => (row.stopId = this.initialRows[index].stopId),
      );
      this.syncedStopChains = incomingStopChains;
      return;
    }
    this.flushPendingConfirm();
    this.syncedStopChains = incomingStopChains;
    this.rows = this.initialRows.map((row) =>
      row.breakRow
        ? { name: '', breakRow: true }
        : {
            stopId: row.stopId,
            name: row.name ?? '',
            latitude: row.latitude,
            longitude: row.longitude,
          },
    );
    if (
      this.openedRowIndex != null &&
      (this.openedRowIndex >= this.rows.length ||
        this.rows[this.openedRowIndex].breakRow)
    ) {
      this.openedRowIndex = null;
    }
    const activeSearchStopId = this.activeSearchRow?.stopId;
    this.activeSearchRow =
      activeSearchStopId != null
        ? (this.rows.find((row) => row.stopId === activeSearchStopId) ?? null)
        : null;
    if (
      !this.activeSearchRow ||
      this.activeSearchMatchIndex >=
        this.searchMatchCountOf(this.activeSearchRow)
    )
      this.activeSearchMatchIndex = 0;
    this.lastEmittedKey = JSON.stringify(this.buildStopsPayload().stops);
  }

  private syncOpenedRowWithSelection() {
    const selectedRowIndex =
      this.selectedStopIds?.length === 1
        ? this.rows.findIndex(
            (row) =>
              row.stopId != null && row.stopId === this.selectedStopIds[0],
          )
        : -1;
    if (selectedRowIndex !== -1) {
      if (
        this.openedRowIndex &&
        this.rows[selectedRowIndex].stopId ===
          this.rows[this.openedRowIndex].stopId
      )
        return;
      if (this.openedRowIndex !== selectedRowIndex)
        this.scrollToOpenedRow = true;
      this.openedRowIndex = selectedRowIndex;
    } else if (
      this.openedRowIndex != null &&
      this.rows[this.openedRowIndex]?.stopId != null
    ) {
      this.openedRowIndex = null;
    }
  }

  private revealMissingSegments() {
    this.highlightMissingSegments = true;
    this.scrollToFirstMissingSegment = true;
    this.liveAnnouncer.announce(
      this.literalService.get('services.routes.stopsModal.connectAllStopsBeforeSave', true),
    );
  }

  private refreshMissingSegmentWarnings() {
    if (!this.initialRows?.some((row) => row.breakRow))
      this.highlightMissingSegments = false;
    afterNextRender(() => this.updateMissingSegmentWarnings(), {
      injector: this.injector,
    });
  }

  updateMissingSegmentWarnings() {
    if (!this.highlightMissingSegments || !this.panel || !this.scrollContainer) {
      if (this.missingSegmentWarningTops().length)
        this.missingSegmentWarningTops.set([]);
      return;
    }
    const panelTop = this.panel.nativeElement.getBoundingClientRect().top;
    const visibleArea =
      this.scrollContainer.nativeElement.getBoundingClientRect();
    this.missingSegmentWarningTops.set(
      (this.rowElements ?? [])
        .filter((rowElement, index) => !!this.rows[index]?.breakRow)
        .map((rowElement) => rowElement.nativeElement.getBoundingClientRect())
        .filter(
          (rowArea) =>
            rowArea.height > 0 &&
            rowArea.top >= visibleArea.top &&
            rowArea.bottom <= visibleArea.bottom,
        )
        .map((rowArea) => rowArea.top + rowArea.height / 2 - panelTop),
    );
  }

  ngAfterViewChecked() {
    if (this.scrollToFirstMissingSegment) {
      this.scrollToFirstMissingSegment = false;
      const firstMissingSegmentIndex = this.rows.findIndex(
        (row) => row.breakRow,
      );
      this.rowElements
        ?.get(firstMissingSegmentIndex)
        ?.nativeElement.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      afterNextRender(() => this.updateMissingSegmentWarnings(), {
        injector: this.injector,
      });
    }
    if (this.scrollToOpenedRow && this.openedRow) {
      this.scrollToOpenedRow = false;
      this.openedRow.nativeElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
    if (this.scrollToActiveSearchRow) {
      this.scrollToActiveSearchRow = false;
      const activeRowIndex = this.activeSearchRow
        ? this.rows.indexOf(this.activeSearchRow)
        : -1;
      this.rowElements
        ?.get(activeRowIndex)
        ?.nativeElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
    }
  }

  private rowKey(row: IRouteStopRow) {
    return row.breakRow
      ? 'break'
      : `${row.name ?? ''}|${row.latitude ?? ''}|${row.longitude ?? ''}`;
  }

  private stopChainsOf(rows: IRouteStopRow[]): number[][] {
    const chains: number[][] = [[]];
    rows.forEach((row) => {
      if (row.breakRow) chains.push([]);
      else if (row.stopId != null)
        chains[chains.length - 1].push(Number(row.stopId));
    });
    return chains.filter((chain) => chain.length > 1);
  }

  private get pendingEndRow(): boolean {
    const lastRow = this.rows[this.rows.length - 1];
    return (
      this.rows.length > 1 &&
      !!lastRow &&
      !lastRow.breakRow &&
      !lastRow.name &&
      lastRow.latitude == null &&
      lastRow.longitude == null
    );
  }

  private get pathRows(): IRouteStopRow[] {
    return this.pendingEndRow ? this.rows.slice(0, -1) : this.rows;
  }

  private buildStopsPayload(): IRouteStopsPayload {
    return {
      endStopMissing: this.pendingEndRow,
      stops: this.pathRows
        .filter((row) => !row.breakRow)
        .map((row) => ({
          townStopId:
            row.townStopId != null ? Number(row.townStopId) : undefined,
          name: row.name.trim(),
          latitude: row.latitude!,
          longitude: row.longitude!,
        })),
    };
  }

  private newRow(): IRouteStopRow {
    return { name: '' };
  }

  getStopLabel(index: number): string {
    if (index === 0)
      return this.literalService.get(
        'services.routes.stopsModal.initialStop',
        true,
      );
    if (index === this.rows.length - 1)
      return this.literalService.get(
        'services.routes.stopsModal.finalStop',
        true,
      );
    return `${this.literalService.get('services.routes.stopsModal.stop', true)} ${index + 1}`;
  }

  toggleRow(index: number) {
    this.openedRowIndex = this.openedRowIndex === index ? null : index;
    this.emitOpenedRowSelection();
  }

  addStopAt(index: number) {
    this.rows.splice(index, 0, this.newRow());
    this.openedRowIndex = index;
    this.emitOpenedRowSelection();
  }

  removeStop(index: number) {
    if (this.rows.length > 2) {
      this.rows.splice(index, 1);
      this.openedRowIndex = null;
      this.emitOpenedRowSelection();
      this.scheduleAutoConfirm();
    }
  }

  private emitOpenedRowSelection() {
    this.changedStop = null;
    this.selectStopEventEmitter.emit(
      this.openedRowIndex != null
        ? (this.rows[this.openedRowIndex]?.stopId ?? null)
        : null,
    );
  }

  moveUp(index: number) {
    if (index > 0) {
      [this.rows[index - 1], this.rows[index]] = [
        this.rows[index],
        this.rows[index - 1],
      ];
      if (this.openedRowIndex === index) this.openedRowIndex = index - 1;
      this.scheduleAutoConfirm();
    }
  }

  moveDown(index: number) {
    if (index < this.rows.length - 1) {
      [this.rows[index + 1], this.rows[index]] = [
        this.rows[index],
        this.rows[index + 1],
      ];
      if (this.openedRowIndex === index) this.openedRowIndex = index + 1;
      this.scheduleAutoConfirm();
    }
  }

  onNameTyped(row: IRouteStopRow, value: string) {
    row.name = value;
    row.townStopId = undefined;
    this.openTownStopsDropdown(row, false);
    this.scheduleAutoConfirm();
  }

  toggleTownStopsDropdown(row: IRouteStopRow) {
    if (row.dropdownOptions?.length && row.dropdownOptions[0].kind === 'town') {
      row.dropdownOptions = undefined;
      return;
    }
    this.openTownStopsDropdown(row, true);
  }

  private openTownStopsDropdown(row: IRouteStopRow, showAllWhenEmpty: boolean) {
    const filterText = (row.name ?? '').trim().toLowerCase();
    const matches = this.townStops.filter(
      (stop) => !filterText || stop.name.toLowerCase().includes(filterText),
    );
    const source =
      matches.length > 0 || !showAllWhenEmpty ? matches : this.townStops;
    row.dropdownOptions = source.map((stop) => ({
      value: String(stop.id),
      label: stop.name,
      kind: 'town' as const,
    }));
  }

  async searchPlaces(row: IRouteStopRow) {
    const query = (row.name ?? '').trim();
    if (query.length < 3) return;
    row.searching = true;
    try {
      const suggestions = await this.mapUtilsService.getPlaceSuggestions(
        query,
        this.townLatitude,
        this.townLongitude,
        2,
      );
      row.dropdownOptions = suggestions.map((suggestion) => ({
        value: suggestion.placeId,
        label: suggestion.description,
        kind: 'place' as const,
      }));
    } catch {
      row.dropdownOptions = [];
    } finally {
      row.searching = false;
    }
  }

  async selectOption(row: IRouteStopRow, option: IRouteStopOption) {
    if (option.kind === 'town') {
      const stop = this.townStops.find(
        (townStop) => String(townStop.id) === option.value,
      );
      if (!stop) return;
      row.townStopId = String(stop.id);
      row.name = stop.name;
      row.latitude = stop.latitude;
      row.longitude = stop.longitude;
    } else {
      const place = await this.mapUtilsService.getPlaceLocation(option.value);
      row.townStopId = undefined;
      row.name = place.name || option.label;
      row.latitude = place.location.lat;
      row.longitude = place.location.lng;
    }
    row.dropdownOptions = undefined;
    this.changedStop = {
      key: this.rowKey(row),
      rowIndex: this.rows.indexOf(row),
    };
    this.scheduleAutoConfirm();
  }

  isRowValid(row: IRouteStopRow): boolean {
    return (
      !row.breakRow &&
      !!row.name &&
      row.latitude != null &&
      row.longitude != null
    );
  }

  get isValid(): boolean {
    const pathRows = this.pathRows;
    return (
      pathRows.length >= (this.pendingEndRow ? 1 : 2) &&
      pathRows.every((row) => this.isRowValid(row))
    );
  }

  scheduleAutoConfirm() {
    clearTimeout(this.confirmTimeout);
    this.confirmTimeout = null;
    if (!this.isValid) return;
    this.confirmTimeout = setTimeout(() => {
      this.confirmTimeout = null;
      this.emitConfirm();
    }, 800);
  }

  private flushPendingConfirm() {
    if (this.confirmTimeout == null) return;
    clearTimeout(this.confirmTimeout);
    this.confirmTimeout = null;
    this.emitConfirm();
  }

  private emitConfirm() {
    const payload = this.buildStopsPayload();
    const key = JSON.stringify(payload.stops);
    if (key === this.lastEmittedKey) return;
    this.lastEmittedKey = key;
    this.confirmEventEmitter.emit({
      ...payload,
      previousStopChains: this.syncedStopChains,
    });
  }

  reset() {
    this.rows = [this.newRow(), this.newRow()];
    this.openedRowIndex = 0;
    this.lastEmittedKey = '';
    this.setActiveSearchMatch(null);
  }

  onSearchChange(term: string) {
    if (term === this.searchTerm) return;
    this.searchTerm = term;
    this.searchPartsByName.clear();
    this.setActiveSearchMatch(this.searchMatches[0] ?? null);
  }

  onSearchSubmit(term: string) {
    if (term === this.searchTerm) this.goToSearchMatch(1);
    else this.onSearchChange(term);
  }

  goToSearchMatch(step: 1 | -1) {
    const matches = this.searchMatches;
    if (!matches.length) return;
    const currentIndex = this.activeSearchPosition - 1;
    const nextIndex =
      currentIndex === -1
        ? step === 1
          ? 0
          : matches.length - 1
        : (currentIndex + step + matches.length) % matches.length;
    this.setActiveSearchMatch(matches[nextIndex]);
  }

  private setActiveSearchMatch(
    match: { row: IRouteStopRow; matchIndex: number } | null,
  ) {
    this.activeSearchRow = match?.row ?? null;
    this.activeSearchMatchIndex = match?.matchIndex ?? 0;
    this.scrollToActiveSearchRow = !!match;
  }

  get searchMatches(): { row: IRouteStopRow; matchIndex: number }[] {
    if (!this.searchTerm.trim()) return [];
    return this.rows.flatMap((row) =>
      Array.from({ length: this.searchMatchCountOf(row) }, (_, matchIndex) => ({
        row,
        matchIndex,
      })),
    );
  }

  get activeSearchPosition(): number {
    return (
      this.searchMatches.findIndex(
        (match) =>
          match.row === this.activeSearchRow &&
          match.matchIndex === this.activeSearchMatchIndex,
      ) + 1
    );
  }

  isSearchMatch(row: IRouteStopRow): boolean {
    return this.searchMatchCountOf(row) > 0;
  }

  isActiveSearchPart(row: IRouteStopRow, part: ITextMatchPart): boolean {
    return (
      part.match &&
      row === this.activeSearchRow &&
      part.matchIndex === this.activeSearchMatchIndex
    );
  }

  private searchMatchCountOf(row: IRouteStopRow): number {
    return row.breakRow
      ? 0
      : this.nameSearchParts(row.name).filter((part) => part.match).length;
  }

  nameSearchParts(name: string): ITextMatchPart[] {
    let parts = this.searchPartsByName.get(name);
    if (!parts) {
      parts = this.utilsService.splitTextMatches(name, this.searchTerm);
      this.searchPartsByName.set(name, parts);
    }
    return parts;
  }
}
